/**
 * Domain-neutral seam that lets another feature hand a prompt to the
 * assistant without importing `AgentPanel` or knowing anything about it (no
 * suggestions/templates concept lives here — see design D6). There is
 * exactly one `AgentPanel` mounted app-wide (`main-routes/Routes.tsx`), so a
 * module-level publish/subscribe list is enough: no context provider to wire
 * through the tree, and any component anywhere can call `ask`.
 */
import React from "react";

export type AgentAskOptions = {
  /**
   * Starts a new conversation for this prompt instead of continuing
   * whichever one is open (the "AI suggestions" button: its question is
   * about the dialog/page at hand, not the earlier chat).
   */
  newConversation?: boolean;
};

type AskListener = (prompt: string, options?: AgentAskOptions) => void;

const listeners = new Set<AskListener>();

/**
 * `AgentPanel` subscribes here on mount (while shown) to open itself and
 * send an incoming prompt. Returns the unsubscribe function.
 */
export const subscribeToAgentPanelAsk = (listener: AskListener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export interface AgentPanelControl {
  /**
   * Opens the assistant panel (if closed) and sends `prompt` through its
   * normal send path — into a fresh conversation with
   * `{ newConversation: true }`. A no-op when no panel is subscribed — e.g.
   * the user lacks assistant access, so `AgentPanel` renders nothing and
   * never subscribes.
   */
  ask: (prompt: string, options?: AgentAskOptions) => void;
}

export function useAgentPanelControl(): AgentPanelControl {
  return React.useMemo(
    () => ({
      ask: (prompt: string, options?: AgentAskOptions) => {
        listeners.forEach(listener => listener(prompt, options));
      },
    }),
    []
  );
}
